<template>
  <div class="cyclotron-viz">
    
    <div class="viz-header" >
      <div class="brief" v-latex="knowledgePoint"></div>
      <div class="formula" v-latex>$<strong>公式：</strong>E_k = (qBR)² / 2m$</div>
    </div>

    <div class="main-layout vertical">
      <div class="canvas-box simulation">
        <svg width="100%" height="240" viewBox="0 0 400 240">
          <!-- Magnetic Field Background (X marks) -->
          <g opacity="0.1">
             <text v-for="x in 10" :key="'x'+x" :x="x*40 - 20" :y="30" fill="#00d2ff" font-size="20">×</text>
             <text v-for="x in 10" :key="'x2'+x" :x="x*40 - 20" :y="120" fill="#00d2ff" font-size="20">×</text>
             <text v-for="x in 10" :key="'x3'+x" :x="x*40 - 20" :y="210" fill="#00d2ff" font-size="20">×</text>
          </g>

          <!-- The Dees (D-shaped electrodes) -->
          <path d="M 195 40 A 80 80 0 1 0 195 200 Z" fill="rgba(255,255,255,0.05)" stroke="#888" />
          <path d="M 205 40 A 80 80 0 1 1 205 200 Z" fill="rgba(255,255,255,0.05)" stroke="#888" />

          <!-- Gap indicator -->
          <rect x="195" y="40" width="10" height="160" :fill="gapColor" opacity="0.3" />

          <!-- Particle Path -->
          <path :d="pathData" fill="none" stroke="#ff4757" stroke-width="2" />
          
          <!-- Particle -->
          <circle :cx="particle.x" :cy="particle.y" r="4" fill="#ff4757" />
        </svg>

        <div class="hud-top">
           <div class="stat">
              <label>末速度 v = qBR/m</label>
           </div>
        </div>
      </div>

      <div class="bottom-controls">
        <div class="control-card glass">
          <div class="row">
             <label>磁感应强度 B: {{ magneticField }} T</label>
             <el-slider v-model="magneticField" :min="10" :max="100" size="mini"></el-slider>
          </div>
          <el-button size="mini" @click="reset">重新注入粒子</el-button>
        </div>

        <div class="discovery-note" v-latex>
          <p><strong>物理内透：</strong>粒子在D形盒内受洛伦兹力做<strong>圆周运动</strong>，周期不变；每经过间隙处受交变电场<strong>加速</strong>，半径增大。最终动能仅由磁场和盒半径决定，与交变电压大小无关。</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'CyclotronViz',
  props: { knowledgePoint: { type: String, default: '' } },
  data() {
    return {
      magneticField: 50,
      particle: { x: 200, y: 120, vx: 2, vy: 0, r: 2 },
      history: [],
      tick: 0,
      running: true
    }
  },
  computed: {
    gapColor() {
      // Alternates based on particle crossing
      return this.particle.vx > 0 ? '#ff4757' : '#2ed573';
    },
    pathData() {
      if (this.history.length < 2) return '';
      return 'M ' + this.history.map(p => `${p.x} ${p.y}`).join(' L ');
    }
  },
  methods: {
    reset() {
      this.particle = { x: 200, y: 120, vx: 2, vy: 0, r: 2 };
      this.history = [];
    },
    update() {
      if (!this.running) return;

      const p = this.particle;
      const b = this.magneticField / 500;

      // Magnetic region
      if (Math.abs(p.x - 200) > 5) {
        // Change in velocity due to Lorentz force
        const ax = p.vy * b;
        const ay = -p.vx * b;
        p.vx += ax;
        p.vy += ay;
      } else {
        // Accelerator gap
        if (p.vx > 0) p.vx += 0.5;
        else p.vx -= 0.5;
      }

      p.x += p.vx;
      p.y += p.vy;

      if (this.tick % 4 === 0) {
        this.history.push({x: p.x, y: p.y});
        if (this.history.length > 500) this.history.shift();
      }

      // Bounds
      const dist = Math.sqrt(Math.pow(p.x - 200, 2) + Math.pow(p.y - 120, 2));
      if (dist > 85) {
          this.reset();
      }

      this.tick++;
      requestAnimationFrame(this.update);
    }
  },
  mounted() { this.update(); },
  beforeDestroy() { this.running = false; }
}
</script>

<style scoped lang="scss">
.cyclotron-viz { flex: 1; display: flex; flex-direction: column; gap: 12px; height: 100%; overflow: hidden; }
.canvas-box { background: #000; border-radius: 12px; position: relative; border: 1px solid rgba(255,255,255,0.05); }
.bottom-controls { flex: 0.6; display: flex; flex-direction: column; gap: 10px; }
.control-card { background: rgba(255,255,255,0.03); border-radius: 12px; padding: 15px; 
  label { font-size: 0.7rem; color: #888; display: block; margin-bottom: 5px; } 
}
.discovery-note { background: rgba(255, 71, 87, 0.05); padding: 12px 18px; border-radius: 12px; p { margin: 0; font-size: 0.75rem; color: #aaa; line-height: 1.5; strong { color: #fff; } } }
</style>
